import { Add, Sort } from '@mui/icons-material';
import {
  EditType,
  type PersonTargetResponse,
  type ProfileEditReviewItemRes,
  ProfileEditType,
  type RecruitmentInterestBase,
  RecruitmentStatus,
  type TopInterestProfileEdit,
} from '@on3/api';
import { Avatar } from '@on3/ui-lib/components/Avatar';
import { Form } from '@on3/ui-lib/components/Form';
import { SortControl } from '@on3/ui-lib/components/SortControl';
import { externalApi, useAuth, useToast } from '@on3/ui-lib/index';
import { isDiff } from '@on3/ui-lib/utils/compare';
import { generateKey } from '@on3/ui-lib/utils/generateKey';
import { PendingTile } from '@public/components/Elite/Profile/Edits/Pending/Tile';
import { useModal } from '@public/components/Modals';
import { usePlayerProfile } from '@public/contexts/PlayerProfile/PlayerProvider';
import { playerAdminModules } from '@public/utils/player';
import {
  getEditOrDefault,
  getProfileUpdates,
  getUpdatedEdits,
  type ProfileEditReviewItem,
} from '@public/utils/player/admin';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { array, boolean, number, object, string } from 'yup';

import styles from './EditsTopInterests.module.scss';

export interface ITopInterestsEdits {
  topInterest: ProfileEditReviewItem<TopInterestProfileEdit>[];
}

interface ProfileTopInterestsProps {
  topInterests: PersonTargetResponse | null;
  pendingEdits: ITopInterestsEdits;
}

export type TopInterest = Omit<
  TopInterestProfileEdit,
  'recruitmentStatus' | 'visits'
> & {
  order: number;
  recruitmentStatus: RecruitmentStatus;
  visits?: TopInterestVisit[];
};

export type TopInterestVisit = {
  type: 'visit' | 'coaching';
  visitKey?: number | null;
  dateOccurred: string;
  official: boolean;
  coaches?: {
    key: number;
    label: string;
  }[];
};

type ProtectedStatusTeams = {
  protectedTeam?: TopInterest;
  signedTeam?: TopInterest;
  enrolledTeam?: TopInterest;
};

export const topInterestSchema = object({
  contacted: boolean().when('recruitmentStatus', {
    is: (rs: RecruitmentStatus) => rs !== RecruitmentStatus.Interested,
    then: (s) => s.transform(() => true),
    otherwise: (s) => s.transform(() => false),
  }),
  offer: boolean().when('recruitmentStatus', {
    is: (rs: RecruitmentStatus) =>
      ![RecruitmentStatus.Interested, RecruitmentStatus.Contacted].includes(rs),
    then: (s) => s.transform(() => true),
    otherwise: (s) => s.transform(() => false),
  }),
  recruitmentStatus: string().oneOf(
    Object.values(RecruitmentStatus),
    'Recruitment status is required',
  ),
  organizationKey: number().required('Team is required'),
  primaryPersonKey: number().nullable().label('Recruiter'),
});

const schema = array().of(topInterestSchema);

const interestRecruitmentStatuses = {
  Interested: RecruitmentStatus.Interested,
  Contacted: RecruitmentStatus.Contacted,
  Offered: RecruitmentStatus.Offered,
  Committed: RecruitmentStatus.Committed,
  Decommitted: RecruitmentStatus.Decommitted,
  Signed: RecruitmentStatus.Signed,
  Enrolled: RecruitmentStatus.Enrolled,
};

export const protectedRecruitmentStatuses = [
  RecruitmentStatus.Committed,
  RecruitmentStatus.Signed,
  RecruitmentStatus.Enrolled,
];

const protectedRecruitmentOptions = {
  [RecruitmentStatus.Committed]: [
    RecruitmentStatus.Committed,
    RecruitmentStatus.Signed,
    RecruitmentStatus.Enrolled,
  ],
  [RecruitmentStatus.Signed]: [
    RecruitmentStatus.Signed,
    RecruitmentStatus.Enrolled,
  ],
  [RecruitmentStatus.Enrolled]: [RecruitmentStatus.Enrolled],
};

const getRecruitmentStatus = (
  item?: RecruitmentInterestBase & TopInterestProfileEdit,
) => {
  if (item?.recruitmentStatus) {
    return item?.recruitmentStatus;
  } else if (item?.status && item?.status !== RecruitmentStatus.None) {
    return item?.status;
  } else if (item?.offer) {
    return RecruitmentStatus.Offered;
  } else if (item?.contacted) {
    return RecruitmentStatus.Contacted;
  }

  return RecruitmentStatus.Interested;
};

export const useRecruitmentStatusOptions = (
  protectedTeam?: TopInterest,
  team?: TopInterest,
) => {
  return Object.values(interestRecruitmentStatuses)
    .filter((status) => {
      // Non-restricted statuses are always allowed
      if (!protectedRecruitmentStatuses.includes(status)) {
        return true;
      }

      // If this is the create modal (no currentSchool), check if Committed+ is already taken
      if (!team) {
        return !protectedTeam;
      }

      // If another school has a Committed+ status, block restricted statuses
      if (
        !!protectedTeam &&
        team.organizationKey !== protectedTeam.organizationKey
      ) {
        return false;
      }

      // Allow restricted statuses if no other school is Committed+ or this is the same org
      return true;
    })
    .map((status) => ({
      label: status,
      value: status,
    }));
};

export const ProfileTopInterests = ({
  topInterests,
  pendingEdits,
}: ProfileTopInterestsProps) => {
  const {
    state: { player },
  } = usePlayerProfile();
  const { user } = useAuth();
  const { notify } = useToast();
  const { showModal, closeModal } = useModal();
  const [resyncKey, setResyncKey] = useState<string | null>(null);
  const [reorder, setReorder] = useState(false);

  const [edits, setEdits] = useState(pendingEdits);

  const formatTopInterests = useCallback(
    (ti: RecruitmentInterestBase[]) =>
      ti
        .map((item, i) => {
          const existingEdit = edits.topInterest.find(
            (t) => t.newValue?.organizationKey === item.organizationKey,
          )?.newValue;

          return {
            $type: 'topInterest',
            editType: EditType.Update,
            order: getEditOrDefault(existingEdit?.order, item.recruitOrder),
            offer: getEditOrDefault(existingEdit?.offer, item.offer),
            contacted: getEditOrDefault(
              existingEdit?.contacted,
              !!item.contacted,
            ),
            organizationKey: item.organizationKey,
            organizationName: item.organizationName,
            organizationAssetUrl: item.organizationAssetUrl,
            primaryPersonKey: getEditOrDefault(
              existingEdit?.primaryPersonKey,
              item.primaryPersonKey,
            ),
            primaryPersonName: getEditOrDefault(
              existingEdit?.primaryPersonName,
              item.primaryPersonName,
            ),
            recruitmentInterestKey: item.key,
            recruitmentStatus: getRecruitmentStatus(
              getEditOrDefault(existingEdit, item),
            ),
            visits: [
              ...(item.visits ?? []).map((v) => ({
                visitKey: v.key,
                type: 'visit',
                dateOccurred: v.dateOccurred,
                official: v.official,
              })),
              ...(item.coachingVisits ?? []).map((v) => ({
                visitKey: v.key,
                type: 'coaching',
                dateOccurred: v.dateOccurred,
                official: false,
                coaches: v.coaches?.map((c) => ({
                  key: c.key,
                  label: c.fullName ?? '',
                })),
              })),
            ],
          } as TopInterest;
        })
        .sort((a, b) => a?.order - b?.order),
    [edits],
  );

  const [values, setValues] = useState<TopInterest[]>(
    formatTopInterests(topInterests?.recruitmentInterests ?? []),
  );
  const prevValues = useRef<TopInterest[]>(values);

  // Find the committed, signed, and enrolled teams in the top interests and pending edits
  const protectedTeam = useMemo(
    () =>
      values.find((item) =>
        protectedRecruitmentStatuses.includes(item.recruitmentStatus),
      ),
    [values],
  );

  /**
   * Transform the top interests to the request format
   * This ensures $type and editType are included first in the object
   */
  const transformInterestsToRequest = useCallback(
    (data: TopInterest[]) =>
      data.map(({ $type, editType, ...edit }) => ({
        $type,
        editType,
        ...edit,
      })) as TopInterestProfileEdit[],
    [],
  );

  const updateEdits = (reviewItem: ProfileEditReviewItemRes) =>
    setEdits((curr) => getUpdatedEdits(curr, 'topInterest', reviewItem));

  const handleSubmit = useCallback(
    async (data: TopInterest[]) => {
      try {
        const updates = getProfileUpdates(
          externalApi,
          { topInterest: transformInterestsToRequest(data) },
          { topInterest: transformInterestsToRequest(prevValues.current) },
          edits,
          player.key,
          ['topInterest'],
          {
            topInterest: 'organizationKey',
          },
        );

        const responses = await Promise.allSettled(updates.map((fn) => fn()));

        responses.forEach((r, i) => {
          if (r.status === 'fulfilled') {
            updateEdits(r.value?.reviewItem);

            setValues((curr) =>
              curr.map((ti) => ({ ...ti, editType: EditType.Update })),
            );
          }
        });

        if (responses.every((r) => r.status === 'rejected')) {
          notify('Failed to update top teams. Please try again.', {
            type: 'error',
          });

          return;
        }

        if (responses.some((r) => r.status === 'rejected')) {
          notify('Some top teams were unable to saved.', { type: 'warning' });

          return;
        }

        notify('Top teams submitted successfully.', { type: 'success' });

        setResyncKey(generateKey());
      } catch (error) {
        notify('Failed to update top teams. Please try again.', {
          type: 'error',
        });
      }
    },
    [edits, notify, player.key, transformInterestsToRequest],
  );

  const handleUpdateInterest = (
    index: number,
    key: keyof TopInterest,
    value: any,
  ) =>
    setValues((curr) =>
      curr.map((item, i) => (i === index ? { ...item, [key]: value } : item)),
    );

  const addTeam = () =>
    showModal('player.team.create', {
      order: values.length + 1,
      existingOrgs: values.map((i) => i.organizationKey),
      protectedTeam,
      updateEdits,
      onComplete: () => {
        setResyncKey(generateKey());
        closeModal();
      },
    });

  const updateInterest = (interest: TopInterest) =>
    showModal('player.team.update', {
      interest,
      protectedTeam,
      updateEdits,
      onComplete: () => {
        setResyncKey(generateKey());
        closeModal();
      },
    });

  const updateVisits = (interest: TopInterest) =>
    showModal('player.team.visits', {
      interest,
      onComplete: () => {
        setResyncKey(generateKey());
        closeModal();
      },
    });

  // Refresh top interest data
  useEffect(() => {
    async function fetchTopInterests() {
      try {
        const response = await playerAdminModules.topInterests(
          externalApi,
          player.recruitmentKey as number,
          user?.requestParams,
        );

        return response;
      } catch (error) {
        console.error('Failed to fetch top interests', error);
        notify('Failed to sync top interests.', { type: 'error' });
      }
    }

    if (resyncKey) {
      fetchTopInterests().then((res) => {
        const updatedTopInterests = formatTopInterests(
          res?.recruitmentInterests ?? [],
        );

        prevValues.current = updatedTopInterests;
        setValues(updatedTopInterests);
        setResyncKey(null);
      });
    }
  }, [
    formatTopInterests,
    notify,
    player.recruitmentKey,
    resyncKey,
    user?.requestParams,
  ]);

  return (
    <section className={styles.block}>
      <header className={styles.header}>
        <h1>Top Teams</h1>
        <div className={styles.actions}>
          {values.length > 0 && (
            <button
              className={styles.actionBtn}
              disabled={!!resyncKey}
              name="add-team"
              onClick={() => setReorder(!reorder)}
              type="button"
            >
              <Sort />
              {reorder ? 'Reordering' : 'Reorder'}
            </button>
          )}
          <button
            className={styles.actionBtn}
            disabled={!!resyncKey}
            name="add-team"
            onClick={addTeam}
            type="button"
          >
            <Add />
            Add team
          </button>
          {edits.topInterest.length > 0 && (
            <PendingTile
              edits={edits.topInterest.map((ti) => ({
                $type: ProfileEditType.TopInterest,
                key: ti.key,
                message: `${ti.newValue?.organizationName} edit(s) in review.`,
              }))}
            />
          )}
        </div>
      </header>
      {values.length > 0 ? (
        <Form
          className={clsx(styles.form, {
            [styles.disabled]: !!resyncKey,
          })}
          onSubmit={handleSubmit}
          schema={schema}
          values={values}
        >
          {({ processing }) => (
            <>
              <div
                className={clsx(styles.topInterests, {
                  [styles.reorder]: reorder,
                })}
              >
                {values.map((item, i) => (
                  <div
                    className={styles.interest}
                    data-order={i + 1}
                    key={item.organizationKey}
                  >
                    <div className={styles.content}>
                      <div className={styles.meta}>
                        {reorder && (
                          <SortControl
                            index={i}
                            onSortChange={(sorted) =>
                              setValues(sorted as TopInterest[])
                            }
                            showArrows={false}
                            sort={i + 1}
                            sortKey="order"
                            sortable={values}
                          />
                        )}
                        <figure className={styles.logo}>
                          <Avatar
                            altText={`${item.organizationName} logo`}
                            height={32}
                            source={item.organizationAssetUrl}
                            teamAvatar
                            width={32}
                          />
                        </figure>
                        <h2>{item.organizationName}</h2>
                      </div>
                      <div className={styles.details}>
                        <button
                          className={clsx(styles.recruiter, {
                            [styles.disabled]: edits.topInterest.find(
                              (t) =>
                                t.newValue?.organizationKey ===
                                item.organizationKey,
                            ),
                          })}
                          onClick={() => updateInterest(item)}
                          type="button"
                        >
                          <dl>
                            <dt>Recruited by</dt>
                            <dd>{item.primaryPersonName || '-'}</dd>
                          </dl>
                        </button>
                        <button
                          className={clsx(styles.recruitStatus, {
                            [styles.disabled]: edits.topInterest.find(
                              (t) =>
                                t.newValue?.organizationKey ===
                                item.organizationKey,
                            ),
                          })}
                          onClick={() => updateInterest(item)}
                          type="button"
                        >
                          <dl>
                            <dt>Recruitment status</dt>
                            <dd>{item.recruitmentStatus || '-'}</dd>
                          </dl>
                        </button>
                        <button
                          className={clsx(styles.visits, {
                            [styles.disabled]: edits.topInterest.find(
                              (t) =>
                                t.newValue?.organizationKey ===
                                item.organizationKey,
                            ),
                          })}
                          onClick={() => updateVisits(item)}
                          type="button"
                        >
                          <dl>
                            <dt>Visits</dt>
                            <dd>{item.visits?.length || 0}</dd>
                          </dl>
                        </button>
                      </div>
                      <div className={styles.status}>
                        {!protectedTeam && i < 5 && (
                          <button
                            className={styles.commitBtn}
                            name="commit"
                            onClick={() =>
                              handleUpdateInterest(
                                i,
                                'recruitmentStatus',
                                RecruitmentStatus.Committed,
                              )
                            }
                            type="button"
                          >
                            Commit
                          </button>
                        )}
                        {protectedTeam?.recruitmentStatus ===
                          RecruitmentStatus.Committed &&
                          protectedTeam.organizationKey ===
                            item.organizationKey && (
                            <button
                              className={styles.decommitBtn}
                              name="decommit"
                              onClick={() =>
                                handleUpdateInterest(
                                  i,
                                  'recruitmentStatus',
                                  RecruitmentStatus.Decommitted,
                                )
                              }
                              type="button"
                            >
                              Decommit
                            </button>
                          )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <button
                disabled={processing || !isDiff(prevValues.current, values)}
                type="submit"
              >
                {processing ? 'Saving...' : 'Save'}
              </button>
            </>
          )}
        </Form>
      ) : (
        <div className={clsx(styles.topInterests, styles.placeholder)}>
          <div className={styles.interest} data-order={1}>
            <div className={styles.content}>
              <div className={styles.meta}>
                <figure className={styles.logo}>
                  <Avatar
                    altText="Org logo"
                    height={32}
                    teamAvatar
                    width={32}
                  />
                </figure>
                <h2>Add your top teams</h2>
              </div>
              <div className={styles.details}>
                <dl className={styles.recruiter}>
                  <dt>Recruited by</dt>
                  <dd>-</dd>
                </dl>
                <dl className={styles.recruitStatus}>
                  <dt>Recruitment status</dt>
                  <dd>-</dd>
                </dl>
                <dl className={styles.visits}>
                  <dt>Visits</dt>
                  <dd>-</dd>
                </dl>
              </div>
              <div className={styles.status} />
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
